import { createSelector } from 'reselect'
import { getCurrencyLocaleByCountryCode, getCurrencySymbol, getFormattedCurrency, isDevEnvironment, isLiveEnvironment } from '../../../helper'
import { jwtContainsAnyGroup, jwtContainsAnyRole, jwtGetCompanyData, jwtGetGroupId, jwtGetUsername } from '../../../helper/jwt'
import { DEV_TEST_COMPANIES, END2END_TEST_COMPANIES, LIVE_TEST_COMPANIES } from './constants'
import { staticPages } from '../../reducer/system/constants'
import { hasReservationCapability, isReservationOnly } from '../../../helper/capabilities'

export const selectEventZIndex = store => store.system.eventZIndex

export const selectIsNeededZIndex = (zIndex) => createSelector(
	selectEventZIndex,
	(eventZIndex) => {
		return eventZIndex === zIndex
	}
)

export const selectAccessToken = store => store.core?.auth?.accessToken ?? null

export const selectHasAccessToken = store => !!store.core?.auth?.accessToken

export const selectCompanyIndex = createSelector(
	selectAccessToken,
	(accessToken) => {
		try {
			return jwtGetCompanyData(accessToken)?.index || null
		} catch {
			return null
		}
	}
)

export const selectLoginFormCompanyIndex = store => store.login.companyIndex

export const selectReactAppVersion = store => store.login.reactAppVersion

export const selectIsAuthorized = createSelector(
	selectAccessToken,
	selectCompanyIndex,
	(accessToken, companyIndex) => !!(accessToken && companyIndex)
)

export const selectContainsAnyRole = (roles) => createSelector(
	selectAccessToken,
	(accessToken) => {
		return !!jwtContainsAnyRole(accessToken, roles)
	}
)

export const selectHasAnyGroup = (groups) => createSelector(
	selectAccessToken,
	(accessToken) => {
		return !!jwtContainsAnyGroup(accessToken, groups)
	}
)

export const selectCompanyData = (param = null) => createSelector(
	selectAccessToken,
	(accessToken) => {
		if (param) {
			return jwtGetCompanyData(accessToken)?.[param]
		} else {
			return jwtGetCompanyData(accessToken)
		}
	}
)

export const selectUsername = createSelector(
	selectAccessToken,
	(accessToken) => jwtGetUsername(accessToken)
)

export const selectFormattedCurrency = (value) => createSelector(
	selectCompanyData('countryCode'),
	selectCompanyData('currency'),
	(countryCode, currencyCode) => {
		const currencyLocale = getCurrencyLocaleByCountryCode(countryCode)
		return typeof value === 'number' ? getFormattedCurrency(value, currencyLocale, currencyCode) : 'n/a'
	}
)

export const selectCurrencySymbol = createSelector(
	selectCompanyData('currency'),
	(currencyCode) => {
		return getCurrencySymbol(currencyCode)
	}
)

export const selectIsOldReactAppVersion = createSelector(
	selectReactAppVersion,
	version => {
		return version !== window.REACT_APP_VERSION
	}
)

export const selectIsTestCompany = createSelector(
	selectCompanyIndex,
	companyIndex => {
		if (isDevEnvironment()) {
			return DEV_TEST_COMPANIES.includes(companyIndex)

		} else {
			return LIVE_TEST_COMPANIES.includes(companyIndex)
		}
	}
)

export const selectIsAustrianCompany = createSelector(
	selectCompanyData('countryCode'),
	countryCode => countryCode === 'AUT',
)

export const selectIsGermanCompany = createSelector(
	selectCompanyData('countryCode'),
	countryCode => countryCode === 'DEU',
)

export const selectIsEnd2EndTestCompany = createSelector(
	selectCompanyIndex,
	companyIndex => {
		return END2END_TEST_COMPANIES.includes(companyIndex)
	}
)

export const selectIsDevTestCompany = createSelector(
	selectCompanyIndex,
	companyIndex => {
		if (isDevEnvironment()) {
			return ['FA-38264', '52-57403', 'F7-52113', '8A-56702', '8D-66054', '65-41407', '88-42217', '38-87613',
				'94-52553', 'F9-26586', 'FF-81574', '26-46164', '11-67773', 'FF-81574', '3E-65108', '36-21165', '83-30206',
				'D3-50576', '21-24324', '00-44342', '2A-37634', '95-55086', '26-21201', '6E-58866'].includes(companyIndex)
		} else {
			return false
		}
	}
)

export const selectDealerIndex = store => store.support?.dealerContacts?.dealerIndex

export const selectIsMTECompany = createSelector(
	selectCompanyIndex,
	selectDealerIndex,
	(companyIndex, dealerIndex) => {
		const IsMTEClientCompany = dealerIndex === 'DE-55563'
		return isLiveEnvironment() && (IsMTEClientCompany || companyIndex === 'DE-55563')
	},
)

export const selectColorTheme = store => store.system.colorTheme

export const selectNameValidationErrors = store => store.system.nameValidationErrors

export const selectPages = store => store.system.pages

export const selectUserPages = createSelector(
	selectPages,
	(pages) => {
		return staticPages.filter(sPage => {
			// keeps this cash desk list in step with the library one, where the same page is BACKOFFICE
			if (sPage.id === 'OFFICE_REPORTS') {
				return pages.includes('REPORTS')
			} else {
				return pages.includes(sPage.id)
			}
		})
	},
)

export const selectGroupId = createSelector(
	selectAccessToken,
	(accessToken) => {
		return jwtGetGroupId(accessToken)
	}
)

export const selectUserData = store => store.core?.user?.userData ?? null

export const selectIsReservationOnly = createSelector(
	selectUserData,
	(userData) => isReservationOnly(userData)
)

export const selectHasReservationCapability = createSelector(
	selectUserData,
	(userData) => hasReservationCapability(userData)
)

